import { Select } from "@xola/ui-kit";
import { useField, useFormikContext } from "formik";
import { sortBy } from "lodash";
import React, { useMemo } from "react";
import { FieldType, FieldWithPickupOptions } from "types";
import { FormField, useError } from "../Field/FormField";
import { QuestionnaireDescription } from "./QuestionnaireDescription";
import { FieldProps } from "./TextField";

export const PickupLocationField = ({ name, field, classNames }: FieldProps) => {
    const pickupField = field as unknown as FieldWithPickupOptions;
    const [{ value }, , helpers] = useField<string>(name);
    const { setFieldValue } = useFormikContext();
    const error = useError(name);

    const options = useMemo(
        () =>
            sortBy(
                (pickupField.options ?? []).filter((option) => option.pickupLocation?.id),
                "sequence",
            ),
        [pickupField.options],
    );

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedId = event.target.value;
        helpers.setValue(selectedId);

        // The API keys the response off `pickupLocation`, not `value`, so both have to move together.
        const responsePath = name.replace(/\.value$/, "");
        if (responsePath !== name) {
            const selected = options.find((option) => option.pickupLocation.id === selectedId);
            setFieldValue(
                `${responsePath}.pickupLocation`,
                selected ? { id: selected.pickupLocation.id, name: selected.pickupLocation.name } : undefined,
            );
            // A response saved by an older integration can carry type "text"; normalize it on edit.
            setFieldValue(`${responsePath}.type`, FieldType.PICKUP);
        }
    };

    return (
        <FormField label={field.label} name={name} classNames={{ label: classNames?.label }}>
            <QuestionnaireDescription field={field} />

            <Select isError={!!error} value={value ?? ""} onChange={handleChange}>
                {!value && <option value="" />}

                {options.map((option) => (
                    <option key={option.pickupLocation.id} value={option.pickupLocation.id}>
                        {option.pickupLocation.name}
                    </option>
                ))}
            </Select>
        </FormField>
    );
};
